import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Plus, Search, X, BookOpen, ArrowRight, Trash2, Ban, RotateCcw, CircleDot, Info,
} from "lucide-react";
import MOShell, {
  useMOAccess, isWritable, LoadingBlock, NoAccessScreen,
} from "@/components/masterOrder/MOShell";
import { Field, Input } from "@/pages/masterOrder/MOClients";
import {
  listRecipes, getRecipe, createRecipe, updateRecipe,
  listIngredients,
  PURCHASE_UNITS_BY_FAMILY, unitFamily,
  RECIPE_YIELD_FAMILIES, yieldUnitFamily, scalingMethodsForFamily,
  SCALING_METHOD_LABELS, mapCostingError,
} from "@/lib/masterOrderApi";
import { formatApiError } from "@/lib/api";

const showErr = (e) => toast.error(mapCostingError(e) || formatApiError(e));

function StatusPill({ active }) {
  return (
    <span
      className="inline-flex items-center gap-1 text-[10.5px] px-2 py-0.5 rounded-full"
      style={
        active
          ? { background: "#DDE6D3", color: "#3E5236" }
          : { background: "#EDE0CE", color: "#8B6F4B" }
      }
      data-testid={`mo-recipe-status-${active ? "active" : "inactive"}`}
    >
      <CircleDot size={9} strokeWidth={2.4} /> {active ? "Активна" : "Неактивна"}
    </span>
  );
}

/* --------------------- Ingredient search picker inline --------------------- */
function IngredientPicker({ ingredients, value, onPick, testid }) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const selected = ingredients.find((i) => i.id === value);
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return ingredients
      .filter((i) => i.is_active !== false)
      .filter((i) => !s || (i.name || "").toLowerCase().includes(s))
      .slice(0, 40);
  }, [ingredients, q]);
  return (
    <div className="relative">
      <button type="button" onClick={() => setOpen((o) => !o)}
              className="w-full h-9 px-2 rounded text-[12px] text-left flex items-center justify-between gap-1 bg-white"
              style={{ boxShadow: "inset 0 0 0 1px #EFE4D0", color: selected ? "#2A1D14" : "#8B6F4B" }}
              data-testid={testid}>
        <span className="truncate">{selected ? `${selected.name} · ${selected.base_unit}` : "— выбрать —"}</span>
        <ArrowRight size={11} className="rotate-90 shrink-0" color="#8B6F4B" />
      </button>
      {open && (
        <div className="absolute z-20 left-0 right-0 mt-1 rounded-lg bg-white overflow-hidden"
             style={{ boxShadow: "0 12px 30px -12px rgba(35,23,16,0.35), inset 0 0 0 1px #EFE4D0" }}>
          <div className="p-2 border-b" style={{ borderColor: "#EFE4D0" }}>
            <div className="relative">
              <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2" color="#8B6F4B" />
              <input autoFocus value={q} onChange={(e) => setQ(e.target.value)}
                     placeholder="Поиск ингредиента..."
                     className="w-full h-8 pl-7 pr-2 rounded-md text-[12px] outline-none bg-[#FBF5EA]"
                     style={{ boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#2A1D14" }}
                     data-testid={`${testid}-search`} />
            </div>
          </div>
          <div className="max-h-60 overflow-y-auto">
            {filtered.length === 0 ? (
              <div className="px-3 py-3 text-[11.5px]" style={{ color: "#8B6F4B" }}>Ничего не найдено</div>
            ) : (
              filtered.map((i) => (
                <button key={i.id} type="button"
                        onClick={() => { onPick(i.id); setOpen(false); setQ(""); }}
                        className={`w-full text-left px-3 py-1.5 text-[12px] hover:bg-[#FBF5EA] ${i.id === value ? "bg-[#F5EBD8]" : ""}`}
                        style={{ color: "#2A1D14" }}
                        data-testid={`${testid}-option-${i.id}`}>
                  {i.name} <span className="text-[10.5px]" style={{ color: "#8B6F4B" }}>· {i.base_unit}</span>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/* ============================== Recipe Drawer =========================== */
function RecipeDrawer({ open, mode, initial, ingredients, onClose, onSaved }) {
  const emptyLine = { ingredient_id: "", quantity: "", unit: "", notes: "" };
  const [form, setForm] = useState({
    name: "", category: "", yield_quantity: "", yield_unit: "g",
    scaling_method: "manual", notes: "", is_active: true, lines: [{ ...emptyLine }],
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (mode === "edit" && initial) {
      setForm({
        name: initial.name || "",
        category: initial.category || "",
        yield_quantity: initial.yield_quantity != null ? String(initial.yield_quantity) : "",
        yield_unit: initial.yield_unit || "g",
        scaling_method: initial.scaling_method || "manual",
        notes: initial.notes || "",
        is_active: initial.is_active !== false,
        lines: (initial.ingredient_lines || []).map((l) => ({
          ingredient_id: l.ingredient_id,
          quantity: String(l.quantity ?? ""),
          unit: l.unit || "",
          notes: l.notes || "",
        })),
      });
    } else {
      setForm({
        name: "", category: "", yield_quantity: "", yield_unit: "g",
        scaling_method: "manual", notes: "", is_active: true, lines: [{ ...emptyLine }],
      });
    }
  }, [open, mode, initial]);

  if (!open) return null;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setLine = (idx, key, val) => setForm((f) => {
    const lines = f.lines.slice();
    lines[idx] = { ...lines[idx], [key]: val };
    // When ingredient changes, reset unit to first compatible option
    if (key === "ingredient_id") {
      const ing = ingredients.find((i) => i.id === val);
      const fam = ing ? unitFamily(ing.base_unit) : "mass";
      const opts = PURCHASE_UNITS_BY_FAMILY[fam] || [];
      lines[idx].unit = opts[0]?.value || "";
    }
    return { ...f, lines };
  });
  const addLine = () => setForm((f) => ({ ...f, lines: [...f.lines, { ...emptyLine }] }));
  const removeLine = (idx) => setForm((f) => ({ ...f, lines: f.lines.filter((_, k) => k !== idx) }));

  const yieldFamily = yieldUnitFamily(form.yield_unit);
  const yieldUnitOptions = RECIPE_YIELD_FAMILIES.find((f) => f.value === yieldFamily)?.units || [];
  const compatibleScalings = scalingMethodsForFamily(yieldFamily);
  // Snap scaling_method to compatible option if user picks an incompatible one.
  if (!compatibleScalings.includes(form.scaling_method)) {
    // Defer to microtask to avoid setState during render
    Promise.resolve().then(() => setForm((f) => ({ ...f, scaling_method: compatibleScalings[0] || "manual" })));
  }

  const submit = async () => {
    if (!form.name.trim()) { toast.error("Введите название рецептуры"); return; }
    if (!form.yield_quantity || Number(form.yield_quantity) <= 0) { toast.error("Укажите положительный выход рецептуры"); return; }
    if (!form.yield_unit) { toast.error("Выберите единицу выхода"); return; }
    const validLines = form.lines.filter((l) => l.ingredient_id && l.quantity && Number(l.quantity) > 0 && l.unit);
    for (const l of form.lines) {
      if (l.ingredient_id && (!l.quantity || Number(l.quantity) <= 0)) {
        toast.error("Количество каждой строки должно быть больше 0"); return;
      }
      if (l.ingredient_id) {
        const ing = ingredients.find((i) => i.id === l.ingredient_id);
        const fam = ing ? unitFamily(ing.base_unit) : null;
        const allowed = fam ? (PURCHASE_UNITS_BY_FAMILY[fam] || []).map((u) => u.value) : [];
        if (l.unit && !allowed.includes(l.unit)) {
          toast.error(`Единица «${l.unit}» несовместима с базовой единицей ингредиента «${ing?.name}»`); return;
        }
      }
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        category: form.category.trim() || null,
        yield_quantity: form.yield_quantity,
        yield_unit: form.yield_unit,
        scaling_method: form.scaling_method,
        notes: form.notes.trim() || null,
        is_active: form.is_active,
        ingredient_lines: validLines.map((l) => ({
          ingredient_id: l.ingredient_id,
          quantity: l.quantity,
          unit: l.unit,
          notes: l.notes?.trim() || null,
        })),
      };
      const saved = mode === "edit" && initial
        ? await updateRecipe(initial.id, payload)
        : await createRecipe(payload);
      toast.success(mode === "edit" ? "Рецептура сохранена" : "Рецептура создана");
      onSaved(saved);
      onClose();
    } catch (e) { showErr(e); } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end" style={{ background: "rgba(35,23,16,0.42)" }} onClick={onClose}>
      <div
        className="h-full w-full sm:w-[560px] bg-white p-5 overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        style={{ boxShadow: "-30px 0 60px -20px rgba(35,23,16,0.4)" }}
        data-testid="mo-recipe-drawer"
      >
        <div className="flex items-center justify-between mb-4">
          <div className="text-[20px]" style={{ fontFamily: "'Cormorant Garamond', serif", color: "#2A1D14" }}>
            {mode === "edit" ? "Редактировать рецептуру" : "Новая рецептура"}
          </div>
          <button onClick={onClose} className="w-9 h-9 grid place-items-center rounded-full hover:bg-black/5" aria-label="Закрыть">
            <X size={16} color="#8B6F4B" />
          </button>
        </div>

        <div className="space-y-3">
          <Field label="Название *">
            <Input value={form.name} onChange={(v) => set("name", v)} testid="mo-recipe-name" placeholder="Ванильный бисквит" />
          </Field>
          <Field label="Категория">
            <Input value={form.category} onChange={(v) => set("category", v)} testid="mo-recipe-category" placeholder="Бисквиты · Крема · Начинки…" />
          </Field>

          <div className="grid grid-cols-[1fr_120px] gap-2">
            <Field label="Выход *">
              <Input value={form.yield_quantity} onChange={(v) => set("yield_quantity", v)} testid="mo-recipe-yield-qty" placeholder="1000" />
            </Field>
            <Field label="Единица *">
              <select value={form.yield_unit} onChange={(e) => set("yield_unit", e.target.value)}
                      className="w-full h-10 px-3 rounded-lg text-[13px] outline-none bg-white"
                      style={{ boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#2A1D14" }}
                      data-testid="mo-recipe-yield-unit">
                {RECIPE_YIELD_FAMILIES.flatMap((f) =>
                  f.units.map((u) => <option key={u.value} value={u.value}>{u.label} ({f.label.toLowerCase()})</option>)
                )}
              </select>
            </Field>
          </div>

          <Field label="Метод масштабирования">
            <select value={form.scaling_method} onChange={(e) => set("scaling_method", e.target.value)}
                    className="w-full h-10 px-3 rounded-lg text-[13px] outline-none bg-white"
                    style={{ boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#2A1D14" }}
                    data-testid="mo-recipe-scaling">
              {compatibleScalings.map((m) => (
                <option key={m} value={m}>{SCALING_METHOD_LABELS[m]}</option>
              ))}
            </select>
            <p className="text-[10.5px] mt-1 flex items-start gap-1" style={{ color: "#8B6F4B" }}>
              <Info size={10} className="mt-0.5 shrink-0" />
              <span>
                {yieldFamily === "count"  && "Штучный выход → доступно масштабирование по количеству."}
                {yieldFamily === "mass"   && "Массовый выход → доступно масштабирование по массе."}
                {yieldFamily === "volume" && "Объёмный выход → доступен только ручной коэффициент."}
              </span>
            </p>
          </Field>

          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-[10.5px] tracking-[0.2em] uppercase" style={{ color: "#8B6F4B" }}>Ингредиенты</label>
              <button type="button" onClick={addLine}
                      className="h-7 px-2.5 rounded-full text-[11px] inline-flex items-center gap-1"
                      style={{ background: "#FFFFFF", color: "#4A3728", boxShadow: "inset 0 0 0 1px #D9C6A6" }}
                      data-testid="mo-recipe-add-line">
                <Plus size={11} /> Строка
              </button>
            </div>
            {ingredients.length === 0 ? (
              <div className="rounded-lg p-3 text-[12px]"
                   style={{ background: "#FBF5EA", color: "#8B6F4B", boxShadow: "inset 0 0 0 1px #EFE4D0" }}>
                Сначала создайте ингредиенты в разделе <b>Ингредиенты</b>.
              </div>
            ) : form.lines.length === 0 ? (
              <div className="rounded-lg p-3 text-[12px]"
                   style={{ background: "#FBF5EA", color: "#8B6F4B", boxShadow: "inset 0 0 0 1px #EFE4D0" }}>
                Добавьте хотя бы одну строку — нажмите «Строка».
              </div>
            ) : (
              <div className="space-y-2" data-testid="mo-recipe-lines">
                {form.lines.map((l, i) => {
                  const ing = ingredients.find((x) => x.id === l.ingredient_id);
                  const fam = ing ? unitFamily(ing.base_unit) : "mass";
                  const unitOpts = PURCHASE_UNITS_BY_FAMILY[fam] || [];
                  return (
                    <div key={i} className="rounded-lg p-2"
                         style={{ background: "#FBF5EA", boxShadow: "inset 0 0 0 1px #EFE4D0" }}
                         data-testid={`mo-recipe-line-${i}`}>
                      <IngredientPicker ingredients={ingredients} value={l.ingredient_id}
                                        onPick={(id) => setLine(i, "ingredient_id", id)}
                                        testid={`mo-recipe-line-${i}-ing`} />
                      <div className="grid grid-cols-[1fr_80px_28px] gap-2 mt-1.5">
                        <input value={l.quantity} onChange={(e) => setLine(i, "quantity", e.target.value)}
                               placeholder="Количество"
                               className="h-9 px-2 rounded text-[12px] outline-none bg-white"
                               style={{ boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#2A1D14" }}
                               data-testid={`mo-recipe-line-${i}-qty`} />
                        <select value={l.unit} onChange={(e) => setLine(i, "unit", e.target.value)}
                                disabled={!ing}
                                className="h-9 px-1 rounded text-[12px] bg-white disabled:opacity-50"
                                style={{ boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#2A1D14" }}
                                data-testid={`mo-recipe-line-${i}-unit`}>
                          {!ing && <option value="">—</option>}
                          {unitOpts.map((u) => <option key={u.value} value={u.value}>{u.label}</option>)}
                        </select>
                        <button type="button" onClick={() => removeLine(i)}
                                className="w-9 h-9 grid place-items-center rounded"
                                style={{ color: "#8A3F1E" }}
                                aria-label="Удалить строку"
                                data-testid={`mo-recipe-line-${i}-remove`}>
                          <Trash2 size={12} />
                        </button>
                      </div>
                      {ing && (
                        <div className="text-[10.5px] mt-1" style={{ color: "#8B6F4B" }}>
                          Базовая единица ингредиента: <b>{ing.base_unit}</b> · допустимые единицы строки: {unitOpts.map((u) => u.value).join(" / ")}.
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <Field label="Заметки">
            <textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} rows={2}
                      className="w-full px-3 py-2 rounded-lg text-[13px] outline-none resize-none"
                      style={{ background: "#FBF5EA", boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#2A1D14" }}
                      data-testid="mo-recipe-notes" />
          </Field>
          <label className="flex items-center gap-2 text-[13px] cursor-pointer select-none" style={{ color: "#2A1D14" }}>
            <input type="checkbox" checked={form.is_active}
                   onChange={(e) => set("is_active", e.target.checked)}
                   data-testid="mo-recipe-active" />
            <span>Активна (доступна в калькуляциях)</span>
          </label>
        </div>

        <div className="flex justify-end gap-2 mt-6 sticky bottom-0 bg-white pt-3">
          <button onClick={onClose} className="h-10 px-4 rounded-full text-[12.5px]"
                  style={{ background: "#FFFFFF", color: "#4A3728", boxShadow: "inset 0 0 0 1px #D9C6A6" }}>
            Отменить
          </button>
          <button onClick={submit} disabled={saving}
                  className="h-10 px-5 rounded-full text-[12.5px] disabled:opacity-60"
                  style={{ background: "#4A3728", color: "#F7EFE0" }}
                  data-testid="mo-recipe-save">
            {saving ? "Сохраняем..." : "Сохранить"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ============================== Detail Drawer =========================== */
function RecipeDetail({ open, recipe, canWrite, ingredients, onClose, onEdit, onUpdated }) {
  const [busy, setBusy] = useState(false);
  if (!open || !recipe) return null;
  const ingMap = Object.fromEntries(ingredients.map((i) => [i.id, i]));
  const yFam = yieldUnitFamily(recipe.yield_unit);

  const toggleActive = async () => {
    setBusy(true);
    try {
      const upd = await updateRecipe(recipe.id, { is_active: !recipe.is_active });
      toast.success(upd.is_active ? "Рецептура активирована" : "Рецептура деактивирована");
      onUpdated(upd);
    } catch (e) { showErr(e); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-40 flex justify-end" style={{ background: "rgba(35,23,16,0.42)" }} onClick={onClose}>
      <div className="h-full w-full sm:w-[560px] bg-white overflow-y-auto"
           onClick={(e) => e.stopPropagation()}
           style={{ boxShadow: "-30px 0 60px -20px rgba(35,23,16,0.4)" }}
           data-testid="mo-recipe-detail">
        <div className="p-5 border-b" style={{ borderColor: "#EFE4D0" }}>
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="text-[10.5px] tracking-[0.24em] uppercase" style={{ color: "#8B6F4B" }}>Рецептура</div>
              <div className="text-[24px] mt-0.5" style={{ fontFamily: "'Cormorant Garamond', serif", color: "#2A1D14" }}
                   data-testid="mo-recipe-detail-name">{recipe.name}</div>
              <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                <span className="text-[11.5px] px-2 py-0.5 rounded-full" style={{ background: "#F5EBD8", color: "#5B4326" }}>
                  Выход · {recipe.yield_quantity} {recipe.yield_unit}
                </span>
                <span className="text-[11px] px-2 py-0.5 rounded-full" style={{ background: "#EEE5D2", color: "#5B4326" }}>
                  {SCALING_METHOD_LABELS[recipe.scaling_method] || recipe.scaling_method}
                </span>
                <StatusPill active={recipe.is_active !== false} />
                {recipe.category && (
                  <span className="text-[11px] px-2 py-0.5 rounded-full" style={{ background: "#EEE5D2", color: "#5B4326" }}>
                    {recipe.category}
                  </span>
                )}
              </div>
            </div>
            <button onClick={onClose} className="w-9 h-9 grid place-items-center rounded-full hover:bg-black/5" aria-label="Закрыть">
              <X size={16} color="#8B6F4B" />
            </button>
          </div>

          {canWrite && (
            <div className="flex flex-wrap gap-2 mt-4">
              <button onClick={onEdit} className="h-9 px-4 rounded-full text-[12px]"
                      style={{ background: "#4A3728", color: "#F7EFE0" }}
                      data-testid="mo-recipe-detail-edit">
                Редактировать
              </button>
              <button onClick={toggleActive} disabled={busy}
                      className="h-9 px-4 rounded-full text-[12px] inline-flex items-center gap-1.5 disabled:opacity-60"
                      style={{ background: "#FFFFFF", color: "#4A3728", boxShadow: "inset 0 0 0 1px #D9C6A6" }}
                      data-testid="mo-recipe-detail-toggle">
                {recipe.is_active ? <><Ban size={12} /> Деактивировать</> : <><RotateCcw size={12} /> Активировать</>}
              </button>
            </div>
          )}
        </div>

        <div className="p-5">
          <div className="text-[10.5px] tracking-[0.24em] uppercase mb-2" style={{ color: "#8B6F4B" }}>Ингредиенты</div>
          {(recipe.ingredient_lines || []).length === 0 ? (
            <div className="rounded-lg p-4 text-[12.5px]"
                 style={{ background: "#FBF5EA", color: "#8B6F4B", boxShadow: "inset 0 0 0 1px #EFE4D0" }}
                 data-testid="mo-recipe-detail-empty">
              Строк нет. Добавьте ингредиенты в редактировании.
            </div>
          ) : (
            <div className="rounded-lg overflow-hidden" style={{ boxShadow: "inset 0 0 0 1px #EFE4D0" }} data-testid="mo-recipe-detail-lines">
              <table className="w-full text-[12.5px]">
                <thead style={{ background: "#F5EBD8", color: "#5B4326" }}>
                  <tr>
                    <th className="text-left px-3 py-1.5 font-normal">Ингредиент</th>
                    <th className="text-right px-3 py-1.5 font-normal">Кол-во</th>
                    <th className="text-left px-3 py-1.5 font-normal">Ед.</th>
                    <th className="text-right px-3 py-1.5 font-normal">Норм.</th>
                  </tr>
                </thead>
                <tbody>
                  {recipe.ingredient_lines.map((l, i) => {
                    const ing = ingMap[l.ingredient_id];
                    return (
                      <tr key={i} className="border-t" style={{ borderColor: "#EFE4D0" }}
                          data-testid={`mo-recipe-detail-row-${i}`}>
                        <td className="px-3 py-1.5" style={{ color: "#2A1D14" }}>
                          {ing?.name || <span style={{ color: "#8A3F1E" }}>{`Ингредиент ${String(l.ingredient_id).slice(0, 6)}…`}</span>}
                        </td>
                        <td className="px-3 py-1.5 text-right tabular-nums" style={{ color: "#4A3728" }}>{l.quantity}</td>
                        <td className="px-3 py-1.5" style={{ color: "#4A3728" }}>{l.unit}</td>
                        <td className="px-3 py-1.5 text-right tabular-nums" style={{ color: "#8B6F4B" }}>
                          {l.normalized_quantity} {l.normalized_unit}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          <div className="rounded-lg p-3 mt-4 text-[12px] inline-flex items-start gap-2"
               style={{ background: "#F5EBD8", color: "#5B4326" }}>
            <Info size={12} className="mt-0.5 shrink-0" />
            <span>Эта рецептура может использоваться в калькуляциях. Расчёт себестоимости — в Студии калькуляций.</span>
          </div>

          {recipe.notes && (
            <div className="mt-4">
              <div className="text-[10.5px] tracking-[0.24em] uppercase mb-1" style={{ color: "#8B6F4B" }}>Заметки</div>
              <div className="text-[12.5px] whitespace-pre-wrap" style={{ color: "#4A3728" }}>{recipe.notes}</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* =============================== Page =================================== */
export default function MORecipes() {
  const { mode, loading: aLoad } = useMOAccess();
  const canWrite = isWritable(mode);
  const [items, setItems] = useState([]);
  const [ingredients, setIngredients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [activeFilter, setActiveFilter] = useState("all");
  const [familyFilter, setFamilyFilter] = useState("all");
  const [drawer, setDrawer] = useState({ open: false, mode: "create", recipe: null });
  const [detail, setDetail] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [rs, ings] = await Promise.all([listRecipes(), listIngredients()]);
      setItems(rs);
      setIngredients(ings);
    } catch (e) { showErr(e); } finally { setLoading(false); }
  };
  useEffect(() => {
    if (aLoad) return;
    if (mode === "no_access" || mode === "demo") { setLoading(false); return; }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aLoad, mode]);

  const upsertItem = (updated) => {
    setItems((old) => {
      const idx = old.findIndex((x) => x.id === updated.id);
      if (idx < 0) return [updated, ...old];
      const copy = old.slice(); copy[idx] = updated; return copy;
    });
    setDetail((d) => (d && d.id === updated.id ? updated : d));
  };

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return items
      .filter((r) => {
        if (activeFilter === "active" && r.is_active === false) return false;
        if (activeFilter === "inactive" && r.is_active !== false) return false;
        if (familyFilter !== "all" && yieldUnitFamily(r.yield_unit) !== familyFilter) return false;
        if (!s) return true;
        return (r.name || "").toLowerCase().includes(s) || (r.category || "").toLowerCase().includes(s);
      })
      .sort((a, b) => (a.name || "").localeCompare(b.name || "", "ru"));
  }, [items, q, activeFilter, familyFilter]);

  const openDetail = async (r) => {
    // Fetch fresh copy to guarantee latest ingredient_lines snapshots
    try {
      const full = await getRecipe(r.id);
      setDetail(full);
    } catch (e) { showErr(e); setDetail(r); }
  };

  const renderContent = () => {
    if (aLoad || loading) return <LoadingBlock />;
    if (mode === "no_access" || mode === "demo") return <NoAccessScreen />;
    const hasAnything = items.length > 0;

    return (
      <>
        <div className="flex flex-col md:flex-row md:items-center gap-2 mb-3">
          <div className="relative flex-1">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" color="#8B6F4B" />
            <input value={q} onChange={(e) => setQ(e.target.value)}
                   placeholder="Поиск по названию или категории..."
                   className="w-full h-10 pl-9 pr-4 rounded-full text-[13px] outline-none bg-white"
                   style={{ boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#2A1D14" }}
                   data-testid="mo-recipes-search" />
          </div>
          <select value={activeFilter} onChange={(e) => setActiveFilter(e.target.value)}
                  className="h-10 px-3 rounded-lg text-[13px] outline-none bg-white"
                  style={{ boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#2A1D14" }}
                  data-testid="mo-recipes-active-filter">
            <option value="all">Все</option>
            <option value="active">Активные</option>
            <option value="inactive">Неактивные</option>
          </select>
          <select value={familyFilter} onChange={(e) => setFamilyFilter(e.target.value)}
                  className="h-10 px-3 rounded-lg text-[13px] outline-none bg-white"
                  style={{ boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#2A1D14" }}
                  data-testid="mo-recipes-family-filter">
            <option value="all">Все выходы</option>
            <option value="mass">Масса</option>
            <option value="volume">Объём</option>
            <option value="count">Штуки</option>
          </select>
          {canWrite && (
            <button onClick={() => setDrawer({ open: true, mode: "create", recipe: null })}
                    className="h-10 px-5 rounded-full text-[13px] inline-flex items-center gap-2"
                    style={{ background: "#4A3728", color: "#F7EFE0" }}
                    data-testid="mo-recipes-new">
              <Plus size={15} strokeWidth={1.8} /> Добавить рецептуру
            </button>
          )}
        </div>

        {!hasAnything ? (
          <div className="rounded-xl bg-white p-10 text-center"
               style={{ boxShadow: "inset 0 0 0 1px #EFE4D0" }} data-testid="mo-recipes-empty">
            <BookOpen className="mx-auto" size={22} strokeWidth={1.5} color="#8B6F4B" />
            <div className="mt-3 text-[20px]" style={{ fontFamily: "'Cormorant Garamond', serif", color: "#2A1D14" }}>
              Рецептур пока нет
            </div>
            <p className="text-[12.5px] mt-1.5 max-w-md mx-auto" style={{ color: "#8B6F4B" }}>
              Создайте первую рецептуру, чтобы использовать её в калькуляциях заказов.
            </p>
            {canWrite && (
              <button onClick={() => setDrawer({ open: true, mode: "create", recipe: null })}
                      className="mt-4 h-10 px-5 rounded-full text-[13px] inline-flex items-center gap-2"
                      style={{ background: "#4A3728", color: "#F7EFE0" }}
                      data-testid="mo-recipes-empty-cta">
                <Plus size={14} /> Добавить рецептуру
              </button>
            )}
          </div>
        ) : filtered.length === 0 ? (
          <div className="rounded-xl bg-white p-8 text-center text-[13px]"
               style={{ boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#8B6F4B" }} data-testid="mo-recipes-nomatch">
            Ничего не найдено. Попробуйте изменить поиск или фильтр.
          </div>
        ) : (
          <>
            {/* Desktop table */}
            <div className="hidden md:block rounded-xl overflow-hidden bg-white"
                 style={{ boxShadow: "inset 0 0 0 1px #EFE4D0" }}>
              <table className="w-full text-[12.5px]" data-testid="mo-recipes-table">
                <thead style={{ background: "#F5EBD8", color: "#5B4326" }}>
                  <tr>
                    <th className="text-left px-3 py-2 font-normal">Название</th>
                    <th className="text-right px-3 py-2 font-normal">Выход</th>
                    <th className="text-left px-3 py-2 font-normal">Единица</th>
                    <th className="text-right px-3 py-2 font-normal">Ингредиентов</th>
                    <th className="text-left px-3 py-2 font-normal">Масштабирование</th>
                    <th className="text-left px-3 py-2 font-normal">Статус</th>
                    <th className="w-8"></th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((r) => (
                    <tr key={r.id}
                        className="border-t hover:bg-[#FBF5EA] cursor-pointer"
                        style={{ borderColor: "#EFE4D0" }}
                        onClick={() => openDetail(r)}
                        data-testid={`mo-recipe-row-${r.id}`}>
                      <td className="px-3 py-2" style={{ color: "#2A1D14" }}>
                        <div>{r.name}</div>
                        {r.category && (
                          <div className="text-[10.5px] mt-0.5" style={{ color: "#8B6F4B" }}>{r.category}</div>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums" style={{ color: "#2A1D14" }}>{r.yield_quantity}</td>
                      <td className="px-3 py-2" style={{ color: "#4A3728" }}>{r.yield_unit}</td>
                      <td className="px-3 py-2 text-right tabular-nums" style={{ color: "#4A3728" }}>
                        {(r.ingredient_lines || []).length}
                      </td>
                      <td className="px-3 py-2 text-[11.5px]" style={{ color: "#8B6F4B" }}>
                        {SCALING_METHOD_LABELS[r.scaling_method] || r.scaling_method}
                      </td>
                      <td className="px-3 py-2"><StatusPill active={r.is_active !== false} /></td>
                      <td className="px-3 py-2 text-right"><ArrowRight size={13} color="#8B6F4B" /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile cards */}
            <div className="md:hidden space-y-2" data-testid="mo-recipes-cards">
              {filtered.map((r) => (
                <button key={r.id} onClick={() => openDetail(r)}
                        className="w-full text-left rounded-xl bg-white p-4 hover:bg-[#FBF5EA] transition"
                        style={{ boxShadow: "inset 0 0 0 1px #EFE4D0" }}
                        data-testid={`mo-recipe-card-${r.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="text-[14px] truncate" style={{ color: "#2A1D14" }}>{r.name}</div>
                      <div className="text-[11px] mt-0.5 flex items-center gap-2 flex-wrap" style={{ color: "#8B6F4B" }}>
                        <span>Выход · {r.yield_quantity} {r.yield_unit}</span>
                        <span>· {(r.ingredient_lines || []).length} ингр.</span>
                      </div>
                      {r.category && (
                        <div className="text-[10.5px] mt-1" style={{ color: "#8B6F4B" }}>{r.category}</div>
                      )}
                    </div>
                    <StatusPill active={r.is_active !== false} />
                  </div>
                </button>
              ))}
            </div>
          </>
        )}

        <RecipeDrawer
          open={drawer.open} mode={drawer.mode} initial={drawer.recipe} ingredients={ingredients}
          onClose={() => setDrawer({ open: false, mode: "create", recipe: null })}
          onSaved={(r) => { upsertItem(r); load(); }}
        />
        <RecipeDetail
          open={!!detail} recipe={detail} canWrite={canWrite} ingredients={ingredients}
          onClose={() => setDetail(null)}
          onEdit={() => setDrawer({ open: true, mode: "edit", recipe: detail })}
          onUpdated={(upd) => { upsertItem(upd); }}
        />
      </>
    );
  };

  return (
    <MOShell active="recipes" title="Рецептуры"
             subtitle="Ваши рабочие рецептуры для расчёта себестоимости и масштабирования.">
      {renderContent()}
    </MOShell>
  );
}
